// Per-profile scorecards: the headline cells of the stored report. A cell is
// marked when the report's own findings flagged that kind for the profile —
// the page never re-applies a threshold.

import type { Finding, ProfileScorecard } from '../../services/kbEffectiveness';
import { flaggedKinds, headlineCells } from './format';
import styles from '../KBEffectivenessPage.module.css';

interface Props {
  profiles: ProfileScorecard[];
  findings: Finding[];
}

export function ProfileScorecards({ profiles, findings }: Props) {
  const flagged = flaggedKinds(findings);
  if (profiles.length === 0) {
    return (
      <section className={styles.panel} aria-label="Profile scorecards">
        <div className={styles.panelHead}><h2>Profile scorecards</h2></div>
        <p className={styles.empty}>The report holds no profiles.</p>
      </section>
    );
  }
  const cellLabels = headlineCells(profiles[0]).map((c) => c.label);
  return (
    <section className={styles.panel} aria-label="Profile scorecards">
      <div className={styles.panelHead}>
        <h2>Profile scorecards</h2>
        <span className={styles.meta}>a marked cell is one the run's findings flagged</span>
      </div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>profile</th>
              <th className={styles.num}>convs</th>
              <th className={styles.num}>writes</th>
              {cellLabels.map((label) => (
                <th key={label} className={styles.num}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {profiles.map((card) => {
              const kinds = flagged.get(card.profile_id) ?? new Set();
              return (
                <tr key={card.profile_id}>
                  <td><code className={styles.profileTag}>{card.profile_id}</code></td>
                  <td className={styles.num}>{card.record.conversations}</td>
                  <td className={styles.num}>{card.record.writes}</td>
                  {headlineCells(card).map((cell) => {
                    const hit = cell.kinds.some((k) => kinds.has(k));
                    return (
                      <td
                        key={cell.key}
                        className={`${styles.num} ${hit ? styles.flaggedCell : ''}`}
                        title={cell.detail}
                        data-flagged={hit ? 'true' : undefined}
                      >
                        {cell.value}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
